import { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import { resetLowStockAlertDismissal } from '../utils/lowStockAlert';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
      // Verify token by getting user info
      authAPI.getMe()
        .then(res => {
          setUser(res.data.user);
          setPermissions(res.data.permissions);
          setIsAuthenticated(true);
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData, permissions: userPermissions } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setPermissions(userPermissions);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    resetLowStockAlertDismissal();
    setUser(null);
    setPermissions(null);
    setIsAuthenticated(false);
  };

  const hasPermission = (module, action) => {
    if (!isAuthenticated) return false;
    if (user?.role === 'admin') return true;

    if (permissions && permissions !== 'all') {
      const modulePermission = permissions.find(p => p.module === module);
      if (!modulePermission) return false;

      const actionMap = {
        view: 'can_view',
        add: 'can_add',
        edit: 'can_edit',
        delete: 'can_delete'
      };

      return modulePermission[actionMap[action]] === 1 || modulePermission[actionMap[action]] === true;
    }

    return false;
  };

  const value = {
    user,
    permissions,
    isAuthenticated,
    loading,
    login,
    logout,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
